<script setup lang="ts">
import { Drawer, Button, Select, Fluid } from 'primevue';
import ColorPickerField from 'shared/forms/components/ColorPickerField.vue';
import { storeToRefs } from 'pinia';
import RectanglePanel from './RectanglePanel.vue';
import { useSelectedShapeStore } from '../stores/selectedShapeStore';
import { computed, toRaw } from 'vue';
import StorageAreaPanel from '@/map/components/StorageAreaPanel.vue';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useDeleteStorageAreaConfirm } from '@/storageAreas/composables/deleteStorageAreaConfirm';
import { useDeleteShape, type DeleteShapeRequest } from '@/shapes/api/deleteShape';
import { Assert } from 'shared/assert';
import { useCreateShape, type CreateShapeRequest } from '@/shapes/api/createShape';
import { useUpdateShape, type UpdateShapeRequest } from '@/shapes/api/updateShape';
import { useUpdateStorageArea, type UpdateStorageAreaRequest } from '@/storageAreas/api/updateStorageArea';
import { useSelectedLogYardStore } from '../stores/selectedLogYardStore';
import { useCreateStorageArea, type CreateStorageAreaRequest } from '@/storageAreas/api/createStorageArea';
import { useEditInventoryStore } from '@/storageAreas/logYards/stores/editInventoryStore';
import { ShapeMode } from 'shared/shapes/domain/shapeMode';
import PolygonPanel from './PolygonPanel.vue';
import { useSelectedLogSorterStore } from '../stores/selectedLogSorterStore';
import { useQueryLogSorter } from '@/logSorter/api/queryLogSorter';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useUndoStore, type SetUndoOptions } from 'shared/undo/stores/undoStore';
import { useDeleteStorageArea, type DeleteStorageAreaRequest } from '@/storageAreas/api/deleteStorageArea';
import { useUndoDeleteShape, type UndoDeleteShapeRequest } from '@/shapes/api/undoDeleteShape';
import { useSelectedCoilStackStore } from '../stores/selectedCoilStackStore';
import { useAuthStore } from '@/auth/store/authStore';
import { isBulkStorage, isCoilStorage, isLogYard, isPackagePlaceArea, StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import EditProductionOrderDialog from '@/productionOrders/components/EditProductionOrderDialog.vue';
import { templateRef } from '@vueuse/core';
import { getShapeId } from 'shared/shapes/domain/shapeGeoJson';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import { computeStorageAreaDisplayText } from 'shared/storageAreas/domain/computeDisplayText';
import { computeStoragePlaceDisplayText } from 'shared/storageAreas/domain/computePlaceDisplayText';
import { createStorageArea as buildStorageArea } from 'shared/storageAreas/domain/storageArea';
import { useSelectedBulkStore } from '../stores/selectedBulkStore';
import { useEditBulkInventoryStore } from '@/storageAreas/bulk/stores/editBulkInventoryStore';
import { useSelectedPackageStorageStore } from '../stores/selectedPackageStorageStore';
import { useSelectedProductionLineStore } from '../stores/selectedProductionLineStore';
import UpDownInputNumber from '@/common/components/UpDownInputNumber.vue';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore.ts';
import type { UndoFunction } from 'shared/undo/stores/undoStore';

type StorageAreaModel = NonNullable<ReturnType<typeof storageAreaStore.getById>>;

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const undoStore = useUndoStore();

const selectedShapeStore = useSelectedShapeStore();
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const selectedLogYardStore = useSelectedLogYardStore();
const selectedBulkStore = useSelectedBulkStore();
const selectedPackageStorageStore = useSelectedPackageStorageStore();
const selectedProductionLineStore = useSelectedProductionLineStore();
const selectedCoilStackStore = useSelectedCoilStackStore();
const shapeStore = useShapeStore();
const storageAreaStore = useStorageAreaStore();
const settingStore = useSettingStore();
const actionFailedAlert = useActionFailedAlert();
const deleteStorageAreaConfirm = useDeleteStorageAreaConfirm();
const editInventoryStore = useEditInventoryStore();
const editBulkInventoryStore = useEditBulkInventoryStore();
const logSorterStore = useSelectedLogSorterStore();
const authStore = useAuthStore();
const editProductionOrderDialog = templateRef('editProductionOrderDialog');

const createShape = useCreateShape();
const updateShape = useUpdateShape();
const deleteShape = useDeleteShape();
const undoDeleteShape = useUndoDeleteShape();

const queryLogSorter = useQueryLogSorter();

const createStorageArea = useCreateStorageArea();
const updateStorageArea = useUpdateStorageArea();
const deleteStorageArea = useDeleteStorageArea();

const featureFlags = useFeatureFlagStore();

const disableInvtoryBtn = computed(() => {
  switch (true) {
    case isBulkStorage(type.value):
      return !featureFlags.isBulkEnabled();

    case isLogYard(type.value):
      return !featureFlags.isLogYardEnabled();

    default:
      return true
  };
});

const modes = [
  ShapeMode.Rectangle,
  ShapeMode.Polygon
];

const modeOptions: ShapeModeOption[] = modes.map(t => {
  return {
    text: translations.value.enums.shapeMode(t),
    value: t
  };
});

const {
  showUI,
  name,
  shapeChanged,
  operation,
  activeTab,
  hasStorageArea,
  mode,
  hasLogSorter,
  color,
  colorChanged,
  colorEnabled,
  editMultiple,
  zIndex,
  zIndexChanged,
} = storeToRefs(selectedShapeStore);

const {
  storageAreaChanged,
  storageAreaInvalid,
  type,
  storageAreaId,
  operation: storageAreaOperation
} = storeToRefs(selectedStorageAreaStore);

const changed = computed(() => shapeChanged.value || storageAreaChanged.value);
const invalid = computed(() => storageAreaInvalid.value);

// Editing the storage area z-index shifts its package places by the same delta,
// keeping them relative to (and never below) the area z-index. Handled via an
// explicit input event so the shift reliably runs on every change.
function onAreaZIndexInput(value: number | undefined) {
  const next = value ?? 0;
  const delta = next - zIndex.value;
  zIndex.value = next;
  if (delta !== 0) selectedPackageStorageStore.shiftPlacesZIndex(delta);
  selectedPackageStorageStore.setAreaZIndex(next);
}

const deleteVisible = computed(() => (operation.value === 'edit' || storageAreaOperation.value === 'edit') && authStore.isAdmin);
const saveDisabled = computed(() => !changed.value || invalid.value);
const inventoryVisible = computed(() => storageAreaOperation.value === 'edit');

defineExpose({
  save,
  saveDisabled,
  changed,
  invalid
});

const {
  severity: storageAreaTabSeverity,
  variant: storageAreaTabVariant,
} = tabState('storageArea');
const storageAreaTabVisible = computed(() => hasStorageArea.value);

const {
  severity: shapeTabSeverity,
  variant: shapeTabVariant
} = tabState('shape');
const shapeTabVisible = computed(() => selectedShapeStore.operation !== 'none' && storageAreaTabVisible.value && authStore.isAdmin && type.value != StorageAreaType.LogSorterBin);

const isIntake = computed(() => {
  return type.value === StorageAreaType.LogSorterIntake || type.value === StorageAreaType.SawLineIntake;
});

async function showProductionOrder() {
  const id = storageAreaId.value;
  Assert.truthy(id, 'Storage area id missing, this should not happen!');
  await editProductionOrderDialog.value?.show({
    storageAreaId: id
  });
}

interface ShapeModeOption {
  value: ShapeMode;
  text: string;
}

type CreateStorageAreaData = CreateStorageAreaRequest['storageArea'];
type PlaceAreaContext = Parameters<typeof computeStoragePlaceDisplayText>[2];

function resolveAllowedProducts(products: { id: string }[], allowMultiple: boolean) {
  const ids = products.map(p => p.id);
  return allowMultiple ? ids : ids.slice(0, 1);
}

// Shared by PackageStorage (packageStorageData) and PackageOutput/Intake (packagePlaceData)
function buildPackagePlaceFields(areaContext: PlaceAreaContext) {
  const s = selectedPackageStorageStore;
  if (s.operation === 'none') return null;

  const formula = settingStore.setting.sharedClient.storageArea.placeDisplayTextFormula;

  return {
    defaultPlaceLength: s.defaultPlaceLength,
    defaultPlaceWidth: s.defaultPlaceWidth,
    defaultMaxStackCount: s.defaultMaxStackCount,
    defaultMaxWeight: s.defaultMaxWeight,
    defaultMaxHeight: s.defaultMaxHeight,
    places: s.places.map(place => ({
      ...place,
      displayText: computeStoragePlaceDisplayText(formula, place, areaContext),
    })),
  };
}

function applyTypeSpecificData(sa: CreateStorageAreaData, areaContext: PlaceAreaContext) {
  const { type } = sa;

  switch (true) {
    case isLogYard(type): {
      const s = selectedLogYardStore;
      if (s.operation === 'none') return;

      sa.allowedProducts = resolveAllowedProducts(s.allowedProducts, s.allowMultipleProducts);
      sa.logYardData = {
        allowMultipleProducts: s.allowMultipleProducts,
        disableAutomaticProductChange: !s.automaticProductChange,
        sideArea: s.sideArea,
      };
      return;
    }

    case isBulkStorage(type): {
      const s = selectedBulkStore;
      if (s.operation === 'none') return;

      sa.allowedProducts = resolveAllowedProducts(s.allowedProducts, s.allowMultipleProducts);
      sa.bulkStorageData = {
        allowMultipleProducts: s.allowMultipleProducts,
        disableAutomaticProductChange: !s.automaticProductChange,
        maxWeight: s.maxWeight,
        maxVolume: s.maxVolume,
      };
      return;
    }

    case isCoilStorage(type): {
      const s = selectedCoilStackStore;
      if (s.operation === 'none') return;

      sa.coilStack = {
        slots: s.slots,
        slotWidth: s.slotWidth,
        maxHeight: s.maxHeight,
        maxLevels: s.maxLevels,
        maxCoilDiameter: s.maxCoilDiameter,
      };
      return;
    }

    case type === StorageAreaType.PackageStorage:
      sa.packageStorageData = buildPackagePlaceFields(areaContext);
      return;

    case type === StorageAreaType.PackageOutput || type === StorageAreaType.PackageIntake: {
      const fields = buildPackagePlaceFields(areaContext);
      if (fields) {
        sa.packagePlaceData = {
          productionLineId: selectedStorageAreaStore.productionLineId ?? null,
          ...fields,
        };
      }
      return;
    }

    case type === StorageAreaType.ProductionLine: {
      const s = selectedProductionLineStore;
      sa.productionLineData = {
        simulationEnabled: s.simulationEnabled,
        simulationIntervalSeconds: s.simulationIntervalSeconds,
        simulationOutputPlaceId: s.simulationOutputPlaceId,
        simulationProductId: s.simulationProductId,
        simulationPiecesCount: s.simulationPiecesCount,
        simulationPackagingId: s.simulationPackagingId,
        simulationPackageTypeId: s.simulationPackageTypeId,
        simulationLength: s.simulationLength,
        simulationWidth: s.simulationWidth,
        simulationHeight: s.simulationHeight,
      };
      return;
    }

    default:
      throw new Error(`Unsupported storage type: ${type}`);
  }
}

async function deleteCreatedStorageArea(storageAreaId: string) {
  const response = await deleteStorageArea.execute({ storageAreaId });
  if (!response.ok) {
    actionFailedAlert.show(response.details);
  }
}

async function saveCreate() {
  const { type, status, active, temporary, name, operation, note, importantNote } = selectedStorageAreaStore;

  if (operation === 'none') {
    await saveShape(null);
    return;
  }

  Assert.definied(active);
  Assert.definied(temporary);

  const areaContext = { ...buildStorageArea(crypto.randomUUID()), name, type, status, active, temporary, note };

  const request: CreateStorageAreaRequest = {
    storageArea: {
      name,
      type,
      status,
      version: null,
      active,
      temporary,
      allowedProducts: [],
      logYardData: null,
      bulkStorageData: null,
      packageStorageData: null,
      productionLineData: null,
      packagePlaceData: null,
      coilStack: null,
      note,
      importantNote,
      displayText: computeStorageAreaDisplayText(
        settingStore.setting.sharedClient.storageArea.displayTextFormula,
        areaContext,
      ),
    },
  };

  applyTypeSpecificData(request.storageArea, areaContext);

  const response = await createStorageArea.execute(request);

  if (!response.ok) {
    actionFailedAlert.show(response.details);
    return;
  }

  const storageAreaId = response.storageArea.id;

  undoStore.set({
    title: translations.value.undo.createStorageAreaTitle,
    description: translations.value.undo.getUndoCreateStorageAreaDescription(name),
    functions: [() => deleteCreatedStorageArea(storageAreaId)],
  });

  await saveShape(storageAreaId);
}

async function saveShape(storageAreaId: string | null) {
  const { geoJson } = selectedShapeStore;
  Assert.definied(geoJson);

  const shapeRequest: CreateShapeRequest = {
    shape: {
      geofenceGeoJson: geoJson.geofence,
      shapeGeoJson: geoJson.shape,
      version: crypto.randomUUID(),
      storageAreaId,
      color: color.value,
      zIndex: zIndex.value,
    }
  };

  const shapeResponse = await createShape.execute(shapeRequest);

  if (!shapeResponse.ok) {
    actionFailedAlert.show(shapeResponse.details);
    return;
  }


  const deleteRequest: DeleteShapeRequest = {
    shapeId: shapeResponse.shape.id
  };

  undoStore.set({
    title: translations.value.undo.createShape.title,
    description: translations.value.undo.createShape.description,
    functions: [async () => {
      const response = await deleteShape.execute(deleteRequest);
      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }
    }]
  });

  await selectedShapeStore.edit({
    shapeId: shapeResponse.shape.id,
    multiple: false
  });
};

function buildUpdateStorageAreaRequest(original: StorageAreaModel): UpdateStorageAreaRequest {
  const {
    type, status, active, temporary, name, note, importantNote,
    editMultiple, nameEnabled, typeEnabled, statusEnabled,
    activeEnabled, noteEnabled, importantNoteEnabled,
  } = selectedStorageAreaStore;

  Assert.definied(active);
  Assert.definied(temporary);

  // Values after taking the "enabled" flags (multi-edit) into account
  const resolved = {
    name: nameEnabled ? name : original.name,
    type: typeEnabled ? type : original.type,
    status: statusEnabled ? status : original.status,
    active: activeEnabled ? active : original.active,
    note: noteEnabled ? note : (original.note ?? ''),
    importantNote: importantNoteEnabled ? importantNote : (original.importantNote ?? false),
  };

  const displayText = computeStorageAreaDisplayText(
    settingStore.setting.sharedClient.storageArea.displayTextFormula,
    { ...original, ...resolved },
  );

  const request: UpdateStorageAreaRequest = {
    id: original.id,
    storageArea: {
      ...resolved,
      version: original.version,
      temporary,
      allowedProducts: [],
      logYardData: null,
      bulkStorageData: null,
      packageStorageData: null,
      productionLineData: null,
      packagePlaceData: null,
      coilStack: null,
      displayText,
    },
  };
  const sa = request.storageArea;

  // ---- Log yard ----
  const {
    operation: logYardOperation,
    allowMultipleProducts: logYardAllowMultipleProducts,
    allowedProducts: logYardAllowedProducts,
    automaticProductChange: logYardAutomaticProductChange,
    sideArea,
    allowMultipleProductsEnabled: logYardAllowMultipleProductsEnabled,
    allowedProductsEnabled: logYardAllowedProductsEnabled,
    automaticProductChangeEnabled: logYardAutomaticProductChangeEnabled,
    sideAreaEnabled,
  } = selectedLogYardStore;

  if (isLogYard(original.type) && logYardOperation !== 'none' && typeEnabled) {
    const useAllowMultipleProducts = editMultiple ? logYardAllowMultipleProductsEnabled : true;
    const useAllowedProducts = editMultiple ? logYardAllowedProductsEnabled : true;
    const useAutomaticProductChange = editMultiple ? logYardAutomaticProductChangeEnabled : true;
    const useSideArea = editMultiple ? sideAreaEnabled : true;

    sa.logYardData = {
      allowMultipleProducts: useAllowMultipleProducts
        ? logYardAllowMultipleProducts
        : original.logYardData?.allowMultipleProducts ?? false,
      disableAutomaticProductChange: useAutomaticProductChange
        ? !logYardAutomaticProductChange
        : original.logYardData?.disableAutomaticProductChange ?? false,
      sideArea: useSideArea ? sideArea : original.logYardData?.sideArea ?? 0,
    };

    if (useAllowedProducts) {
      const ids = logYardAllowedProducts.map(p => p.id);
      sa.allowedProducts = logYardAllowMultipleProducts ? ids : ids.slice(0, 1);
    } else {
      sa.allowedProducts = original.logYardData?.allowedProducts ?? [];
    }
  } else if (original.logYardData) {
    sa.logYardData = {
      allowMultipleProducts: original.logYardData.allowMultipleProducts,
      disableAutomaticProductChange: original.logYardData.disableAutomaticProductChange,
      sideArea: original.logYardData.sideArea,
    };
    sa.allowedProducts = original.logYardData.allowedProducts;
  }

  // ---- Bulk storage ----
  const {
    operation: bulkStorageOperation,
    allowMultipleProducts: bulkAllowMultipleProducts,
    allowMultipleProductsEnabled: bulkAllowMultipleProductsEnabled,
    allowedProducts: bulkAllowedProducts,
    allowedProductsEnabled: bulkAllowedProductsEnabled,
    automaticProductChange: bulkAutomaticProductChange,
    automaticProductChangeEnabled: bulkAutomaticProductChangeEnabled,
    maxWeight,
    maxWeightEnabled,
    maxVolume,
    maxVolumeEnabled,
  } = selectedBulkStore;

  if (isBulkStorage(original.type) && bulkStorageOperation !== 'none' && typeEnabled) {
    const useAllowMultipleProducts = editMultiple ? bulkAllowMultipleProductsEnabled : true;
    const useAllowedProducts = editMultiple ? bulkAllowedProductsEnabled : true;
    const useAutomaticProductChange = editMultiple ? bulkAutomaticProductChangeEnabled : true;
    const useMaxWeight = editMultiple ? maxWeightEnabled : true; // was `maxWeight`
    const useMaxVolume = editMultiple ? maxVolumeEnabled : true;

    sa.bulkStorageData = {
      allowMultipleProducts: useAllowMultipleProducts
        ? bulkAllowMultipleProducts
        : original.bulkStorageData?.allowMultipleProducts ?? false,
      disableAutomaticProductChange: useAutomaticProductChange
        ? !bulkAutomaticProductChange
        : original.bulkStorageData?.disableAutomaticProductChange ?? false,
      maxVolume: useMaxVolume ? maxVolume : original.bulkStorageData?.maxVolume ?? null,
      maxWeight: useMaxWeight ? maxWeight : original.bulkStorageData?.maxWeight ?? null,
    };

    if (useAllowedProducts) {
      const ids = bulkAllowedProducts.map(p => p.id);
      sa.allowedProducts = bulkAllowMultipleProducts ? ids : ids.slice(0, 1);
    } else {
      sa.allowedProducts = original.bulkStorageData?.allowedProducts ?? [];
    }
  } else if (original.bulkStorageData) {
    sa.bulkStorageData = {
      allowMultipleProducts: original.bulkStorageData.allowMultipleProducts,
      disableAutomaticProductChange: original.bulkStorageData.disableAutomaticProductChange,
      maxVolume: original.bulkStorageData.maxVolume,
      maxWeight: original.bulkStorageData.maxWeight,
    };
    sa.allowedProducts = original.bulkStorageData.allowedProducts;
  }

  // ---- Coil stack ----
  const {
    operation: coilStackOperation,
    slots, slotWidth, maxHeight, maxLevels, maxCoilDiameter,
  } = selectedCoilStackStore;

  if (isCoilStorage(original.type) && coilStackOperation !== 'none' && typeEnabled) {
    sa.coilStack = { slots, slotWidth, maxHeight, maxLevels, maxCoilDiameter };
  } else if (original.coilStack) {
    sa.coilStack = {
      slots: original.coilStack.slots,
      slotWidth: original.coilStack.slotWidth,
      maxHeight: original.coilStack.maxHeight,
      maxLevels: original.coilStack.maxLevels,
      maxCoilDiameter: original.coilStack.maxCoilDiameter,
    };
  }

  // ---- Package storage ----
  const {
    operation: packageStorageOperation,
    defaultPlaceLength, defaultPlaceWidth, defaultMaxStackCount,
    defaultMaxWeight, defaultMaxHeight, places,
  } = selectedPackageStorageStore;

  const buildEditedPlaceFields = () => {
    const placeDisplayTextFormula = settingStore.setting.sharedClient.storageArea.placeDisplayTextFormula;
    const placeAreaContext = {
      id: original.id,
      name: resolved.name,
      status: resolved.status,
    };

    return {
      defaultPlaceLength,
      defaultPlaceWidth,
      defaultMaxStackCount,
      defaultMaxWeight,
      defaultMaxHeight,
      places: places.map(place => ({
        ...place,
        displayText: computeStoragePlaceDisplayText(placeDisplayTextFormula, place, placeAreaContext),
      })),
    };
  };

  if (original.type === StorageAreaType.PackageStorage) {
    if (packageStorageOperation !== 'none') {
      sa.packageStorageData = buildEditedPlaceFields();
    } else if (original.packageStorageData) {
      sa.packageStorageData = {
        defaultPlaceLength: original.packageStorageData.defaultPlaceLength,
        defaultPlaceWidth: original.packageStorageData.defaultPlaceWidth,
        defaultMaxStackCount: original.packageStorageData.defaultMaxStackCount,
        defaultMaxWeight: original.packageStorageData.defaultMaxWeight,
        defaultMaxHeight: original.packageStorageData.defaultMaxHeight,
        places: original.packageStorageData.places,
      };
    }
  }

  // ---- Package output / intake ----
  if (isPackagePlaceArea(original.type)) {
    if (packageStorageOperation !== 'none') {
      sa.packagePlaceData = {
        productionLineId: selectedStorageAreaStore.productionLineId
          ?? original.packagePlaceData?.productionLineId
          ?? null,
        ...buildEditedPlaceFields(),
      };
    } else if (original.packagePlaceData) {
      sa.packagePlaceData = { ...original.packagePlaceData };
    }
  }

  // ---- Production line ----
  if (original.type === StorageAreaType.ProductionLine) {
    const s = selectedProductionLineStore;
    if (s.operation !== 'none') {
      sa.productionLineData = {
        simulationEnabled: s.simulationEnabled,
        simulationIntervalSeconds: s.simulationIntervalSeconds,
        simulationOutputPlaceId: s.simulationOutputPlaceId,
        simulationProductId: s.simulationProductId,
        simulationPiecesCount: s.simulationPiecesCount,
        simulationPackagingId: s.simulationPackagingId,
        simulationPackageTypeId: s.simulationPackageTypeId,
        simulationLength: s.simulationLength,
        simulationWidth: s.simulationWidth,
        simulationHeight: s.simulationHeight,
      };
    } else if (original.productionLineData) {
      sa.productionLineData = { ...original.productionLineData };
    }
  }

  return request;
}

function buildUndoStorageAreaRequest(
  original: StorageAreaModel,
  newVersion: UpdateStorageAreaRequest['storageArea']['version']
): UpdateStorageAreaRequest {
  const request: UpdateStorageAreaRequest = {
    id: original.id,
    storageArea: {
      active: original.active,
      allowedProducts: [],
      logYardData: null,
      bulkStorageData: null,
      packageStorageData: null,
      coilStack: null,
      name: original.name,
      status: original.status,
      temporary: original.temporary,
      type: original.type,
      version: newVersion,
      undo: true,
      note: original.note,
      importantNote: original.importantNote,
      displayText: original.displayText,
    },
  };
  const sa = request.storageArea;

  if (original.type === StorageAreaType.PackageStorage) {
    sa.packageStorageData = original.packageStorageData ?? {
      defaultPlaceLength: 0,
      defaultPlaceWidth: 0,
      defaultMaxStackCount: null,
      defaultMaxWeight: null,
      defaultMaxHeight: null,
      places: [],
    };
  }

  if (isPackagePlaceArea(original.type) && original.packagePlaceData) {
    sa.packagePlaceData = original.packagePlaceData;
  }

  if (original.type === StorageAreaType.ProductionLine && original.productionLineData) {
    sa.productionLineData = original.productionLineData;
  }

  const logYard = original.logYardData;
  if (isLogYard(original.type) && logYard) {
    sa.allowedProducts = logYard.allowedProducts;
    sa.logYardData = {
      allowMultipleProducts: logYard.allowMultipleProducts,
      disableAutomaticProductChange: logYard.disableAutomaticProductChange,
      sideArea: logYard.sideArea,
    };
  }

  const bulk = original.bulkStorageData;
  if (isBulkStorage(original.type) && bulk) {
    sa.allowedProducts = bulk.allowedProducts;
    sa.bulkStorageData = {
      allowMultipleProducts: bulk.allowMultipleProducts,
      disableAutomaticProductChange: bulk.disableAutomaticProductChange,
      maxVolume: bulk.maxVolume,
      maxWeight: bulk.maxWeight,
    };
  }

  const coil = original.coilStack;
  if (isCoilStorage(original.type) && coil) {
    sa.coilStack = {
      slots: coil.slots,
      maxHeight: coil.maxHeight,
      maxLevels: coil.maxLevels,
      slotWidth: coil.slotWidth,
      maxCoilDiameter: coil.maxCoilDiameter,
    };
  }

  return request;
}

function getSaveUndoText(shapesSaved: number, storageAreasSaved: number, name: string) {
  const t = translations.value.undo;
  const withCount = (text: string, count: number) => (count > 1 ? `${text} (${count})` : text);

  if (shapesSaved > 0 && storageAreasSaved > 0) {
    return {
      title: t.editStorageAreaTitle,
      description: withCount(t.getUndoEditStorageAreaDescription(name), storageAreasSaved),
    };
  }
  if (shapesSaved > 0) {
    return {
      title: t.updateShape.title,
      description: withCount(t.updateShape.description, shapesSaved),
    };
  }
  return {
    title: t.editStorageAreaTitle,
    description: withCount(t.getUndoEditStorageAreaDescription(name), storageAreasSaved),
  };
}

async function saveEdit() {
  const { selectedShapeIds, allUpdatedShapes, shapeChanged } = selectedShapeStore;
  const { storageAreaId, storageAreaChanged } = selectedStorageAreaStore;

  Assert.definied(selectedShapeIds);

  const idsToReopen = [...selectedShapeIds];
  const tab = selectedShapeStore.activeTab;

  let success = true;
  const undoFunctions: UndoFunction[] = [];
  let shapesSaved = 0;
  let storageAreasSaved = 0;

  try {
    // ---------- Shapes ----------
    if (shapeChanged) {
      for (const updatedShape of allUpdatedShapes) {
        const shapeId = getShapeId(updatedShape.shape);
        if (!shapeId) continue;

        const shape = shapeStore.getById(shapeId);
        if (!shape) continue;

        const originalShape = structuredClone(toRaw(shape));

        const response = await updateShape.execute({
          shapeId,
          shape: {
            version: shape.version,
            geofenceGeoJson: updatedShape.geofence,
            shapeGeoJson: updatedShape.shape,
            storageAreaId: storageAreaId ?? originalShape.storageAreaId,
            color: color.value,
            zIndex: zIndex.value,
          },
        });

        if (!response.ok) {
          actionFailedAlert.show(response.details);
          success = false;
          break;
        }

        const undoRequest: UpdateShapeRequest = {
          shapeId: originalShape.id,
          shape: {
            geofenceGeoJson: originalShape.geofenceGeoJson,
            shapeGeoJson: originalShape.shapeGeoJson,
            storageAreaId: originalShape.storageAreaId,
            version: response.shape.version,
            undo: true,
            color: originalShape.color,
            zIndex: originalShape.zIndex, // was zIndex.value (the new value)
          },
        };

        undoFunctions.push(async () => {
          const undoResponse = await updateShape.execute(undoRequest);
          if (!undoResponse.ok) {
            actionFailedAlert.show(undoResponse.details);
          }
        });
        shapesSaved++;
      }
    }

    // ---------- Storage areas ----------
    if (success && storageAreaId !== undefined && storageAreaChanged) {
      const { selectedStorageAreaIds, editMultiple } = selectedStorageAreaStore;
      const storageAreaIdsToUpdate = editMultiple ? selectedStorageAreaIds : [storageAreaId];

      for (const saId of storageAreaIdsToUpdate) {
        const storageArea = storageAreaStore.getById(saId);
        if (!storageArea) continue;

        const originalStorageArea = structuredClone(toRaw(storageArea));

        const response = await updateStorageArea.execute(
          buildUpdateStorageAreaRequest(originalStorageArea)
        );

        if (!response.ok) {
          actionFailedAlert.show(response.details);
          success = false;
          break;
        }

        const undoRequest = buildUndoStorageAreaRequest(
          originalStorageArea,
          response.storageArea.version
        );

        undoFunctions.push(async () => {
          const undoResponse = await updateStorageArea.execute(undoRequest);
          if (!undoResponse.ok) {
            actionFailedAlert.show(undoResponse.details);
          }
        });
        storageAreasSaved++;
      }
    }
  } finally {
    // Register undo for whatever was actually saved, even after a partial failure.
    if (undoFunctions.length > 0) {
      const { name } = selectedStorageAreaStore;
      undoStore.set({
        ...getSaveUndoText(shapesSaved, storageAreasSaved, name),
        functions: undoFunctions,
      });
    }
  }
  if (success && (shapesSaved > 0 || storageAreasSaved > 0)) {
    await reopenSelection(idsToReopen, tab);
  }
}

async function reopenSelection(ids: string[], tab: 'storageArea' | 'shape') {
  const stillThere = ids.filter((id) => shapeStore.getById(id) !== undefined);
  if (stillThere.length === 0) return;

  if (stillThere.length > 1) {
    await selectedShapeStore.multiEdit(stillThere);
  } else {
    selectedShapeStore.edit({ shapeId: stillThere[0]!, multiple: false });
  }
  selectedShapeStore.activeTab = tab;
}

async function save() {
  const { operation } = selectedShapeStore;

  if (operation === 'create') {
    await saveCreate();
  } else if (operation === 'edit') {
    await saveEdit();
  }
}

async function onDeleteClick() {

  const {
    selectedShapeIds
  } = selectedShapeStore;

  const {
    storageAreaId
  } = selectedStorageAreaStore;

  if (storageAreaId) {

    await deleteStorageAreaConfirm.show({
      storageAreaId
    });

  } else if (selectedShapeIds) {

    const removeShapeResponse = await deleteShape.execute({
      shapeId: selectedShapeIds[0]!,
    });

    if (!removeShapeResponse.ok) {
      actionFailedAlert.show(removeShapeResponse.details);
    } else {

      const undoRequest: UndoDeleteShapeRequest = {
        shapeId: selectedShapeIds[0]!,
      };

      undoStore.set({
        title: translations.value.undo.deleteShape.title,
        description: translations.value.undo.deleteShape.description,
        functions: [async () => {
          const response = await undoDeleteShape.execute(undoRequest);
          if (!response.ok) {
            actionFailedAlert.show(response.details);
            return;
          }
        }]
      })
    }
  }
  selectedShapeStore.deselect(null);
}

async function onEditInventoryClick() {
  const { storageAreaId, type } = selectedStorageAreaStore;
  Assert.definied(storageAreaId);

  if (isLogYard(type)) {
    await editInventoryStore.show({
      storageAreaId: storageAreaId
    });
    return;
  }

  if (isBulkStorage(type)) {
    await editBulkInventoryStore.show({
      storageAreaId: storageAreaId
    });
    return;
  }
}

function tabState(value: string) {
  return {
    severity: tabSeverity(value),
    variant: tabVariant(value)
  };
}

function tabSeverity(value: string) {
  return computed(() => activeTab.value === value ? 'primary' : undefined);
}

function tabVariant(value: string) {
  return computed(() => activeTab.value !== value ? 'outlined' : undefined);
}

async function onOpenLogSorter() {

  const storageAreaId = selectedStorageAreaStore.storageAreaId;

  const response = await queryLogSorter.execute({
    binId: storageAreaId
  });

  if (!response.ok) {
    actionFailedAlert.show(response.details);
    return;
  }

  selectedShapeStore.deselect(null);

  logSorterStore.edit({
    logSorter: response.logSorter
  });


}

function closePanel() {
  selectedShapeStore.deselect(null);
}

</script>

<template>
  <div v-if="showUI" class="shape-drawer">
    <div class="drawer-close">
      <Button icon="pi pi-times" rounded text severity="secondary" aria-label="Close" @click="closePanel" />
    </div>

    <div class="drawer-title">
      {{ name }}
    </div>

    <div class="tabs">
      <Button :hidden="!storageAreaTabVisible" :severity="storageAreaTabSeverity" :variant="storageAreaTabVariant"
        @click="activeTab = 'storageArea'">{{ translations.generic.storageArea
        }}</Button>
      <Button :hidden="!shapeTabVisible" :severity="shapeTabSeverity" :variant="shapeTabVariant"
        @click="activeTab = 'shape'">{{ translations.generic.shape }}</Button>
    </div>

    <div class="body">
      <StorageAreaPanel v-if="activeTab === 'storageArea'"></StorageAreaPanel>
      <div v-if="activeTab === 'shape'">
        <div class="grid grid-cols-2 gap-2 w-full">
          <ColorPickerField class="mb-4" :changed="colorChanged" v-model:model-value="color" v-if="colorEnabled">
          </ColorPickerField>
          <div class="field" :class="{ changed: zIndexChanged }">
            <label>{{ translations.shapes.zIndex }}</label>
            <UpDownInputNumber :model-value="zIndex" @update:model-value="onAreaZIndexInput" />
          </div>
        </div>
        <Select class="mb-4" fluid v-model:model-value="mode" :options="modeOptions" option-value="value"
          option-label="text" @change="selectedShapeStore.modeChanged()" v-if="!editMultiple"></Select>
        <RectanglePanel v-if="mode === ShapeMode.Rectangle && !editMultiple"></RectanglePanel>
        <PolygonPanel v-if="mode === ShapeMode.Polygon" && !editMultiple></PolygonPanel>
      </div>
    </div>

    <div class="actions">
      <Fluid class="grid grid-cols-2 gap-2 w-full">
        <Button v-if="hasLogSorter && authStore.isAdmin" icon="pi pi-sort-amount-up"
          :label="translations.generic.logSorter" class="col-span-2" @click="onOpenLogSorter"></Button>

        <Button v-if="isIntake" :label="translations.generic.productionOrder" icon="pi pi-barcode"
          @click="showProductionOrder" class="col-span-2"></Button>

        <Button :disabled="saveDisabled" class="save-button" :label="translations.generic.save" icon="pi pi-save"
          @click="save"></Button>

        <Button v-if="inventoryVisible" icon="pi pi-search" :label="translations.generic.inventory"
          @click="onEditInventoryClick" :disabled="disableInvtoryBtn"></Button>

        <Button severity="secondary" icon="pi pi-times" :label="translations.generic.close" class="col-start-1"
          @click="closePanel"></Button>

        <Button v-if="deleteVisible" severity="danger" icon="pi pi-trash" :label="translations.generic.delete"
          class="col-start-2" @click="onDeleteClick"></Button>
      </Fluid>
    </div>
  </div>

  <EditProductionOrderDialog ref="editProductionOrderDialog"></EditProductionOrderDialog>
</template>

<style scoped>
.shape-drawer {
  position: relative;
  height: 100% !important;
  width: 100% !important;
  padding: 1em;
  display: flex;
  flex-direction: column;
}

.drawer-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
}

.drawer-title {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.4;

  padding-bottom: 0.75rem;
  margin-bottom: 1rem;

  border-bottom: 1px solid var(--surface-border);
  color: var(--text-color);
}

.tabs {
  display: flex;
  column-gap: .5rem;
}

.body {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: .5rem;
}

.actions {
  margin-top: auto;
  padding-top: 1rem;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: flex-start;
}
</style>
